import { BaseRHFWidgetProps } from "@impero-com/common/components/form/rhfFields/types";
import { t, TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { FieldValues, useController } from "react-hook-form";
import { z } from "zod";

import { ExpectExtends } from "utils/form/expectExtends";
import { extractNestedError } from "utils/form/extractNestedError";

import {
    AllocatedTime,
    AllocatedTimeInput,
    AllocatedTimeInputBaseProps,
} from "../fields/allocatedTimeInput";

const MAX_HOURS = 23;
const MAX_DAYS = 999;

export const getHoursValueSchema = () =>
    z
        .int({
            error: (issue) =>
                issue.input === undefined
                    ? t("Validation_Required")
                    : t("Validation_Invalid"),
        })
        .min(0)
        .max(MAX_HOURS, {
            message: t("AllocatedTime_Value_TooBig", {
                max: MAX_HOURS,
                unit: t("AllocatedTime_Unit_Hours"),
            }),
        });

export const getDaysValueSchema = () =>
    z
        .int({
            error: (issue) =>
                issue.input === undefined
                    ? t("Validation_Required")
                    : t("Validation_Invalid"),
        })
        .min(0)
        .max(MAX_DAYS, {
            message: t("AllocatedTime_Value_TooBig", {
                max: MAX_DAYS,
                unit: t("AllocatedTime_Unit_Days"),
            }),
        });

export const getAllocatedTimeSchema = (t: TFunction) => {
    const hoursSchema = z.object({
        unit: z.literal("Hours"),
        value: getHoursValueSchema(),
    });

    const daysSchema = z.object({
        unit: z.literal("Days"),
        value: getDaysValueSchema(),
    });

    return z.discriminatedUnion("unit", [hoursSchema, daysSchema], {
        error: (issue) => {
            switch (issue.code) {
                case "invalid_union":
                    return t("AllocatedTime_Unit_Invalid");
                default:
                    return t("Validation_Required");
            }
        },
    });
};
export type AllocatedTimeSchema = ExpectExtends<
    z.infer<ReturnType<typeof getAllocatedTimeSchema>>,
    AllocatedTime
>;

export interface RHFAllocatedTimeInputProps<TFieldsValues extends FieldValues>
    extends
        BaseRHFWidgetProps<TFieldsValues, AllocatedTimeSchema>,
        AllocatedTimeInputBaseProps {}

export function RHFAllocatedTimeInput<TFieldsValues extends FieldValues>({
    control,
    name,
    label,
    helpText,
    disabled,
    disableChangeUnit,
    nodeAfter,
}: RHFAllocatedTimeInputProps<TFieldsValues>) {
    const { field, fieldState } = useController({ control, name, disabled });

    const max = ((allocatedTime?: AllocatedTime) => {
        const unit = allocatedTime?.unit;
        switch (unit) {
            case "Hours":
                return MAX_HOURS;
            case "Days":
                return MAX_DAYS;
            case undefined:
                return undefined;
            default:
                assertNever(unit);
        }
    })(field.value);

    return (
        <AllocatedTimeInput
            nodeAfter={nodeAfter}
            label={label}
            helpText={helpText}
            error={extractNestedError(fieldState.error)}
            value={field.value}
            disabled={field.disabled}
            disableChangeUnit={disableChangeUnit}
            onChange={(update) => field.onChange(update(field.value))}
            fieldRef={field.ref}
            onBlur={field.onBlur}
            max={max}
        />
    );
}
